/**
 * useWeekEditor — the week in memory, and the autosave that writes it.
 *
 * One fetch per week, one in-memory model, and edits that coalesce into
 * batches. Every cell edit is optimistic: the grid shows the new officer
 * immediately and the request follows. The server owns hours, pay rate,
 * overtime and status, so whatever comes back replaces what we guessed.
 *
 * Why a queue rather than a request per cell: a 40-post site is ~840 cells and
 * a manager filling a row touches seven at a time. One request per cell would
 * be 840 requests and 840 cross-job overtime recomputes. The batch endpoint
 * runs one recompute per (officer, week) for the whole batch.
 *
 * `blocked` is not an error. The server applies every change it can and
 * returns the ones that need a question asked — an armed post without a
 * current licence, a week crossing forty hours, an officer being replaced with
 * no call-off reason. Those come back here with the original change attached so
 * the dialog can re-send it with the acknowledgement.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { schedulingAPI } from '../../lib/api';

const FLUSH_IDLE_MS = 1100;
// The endpoint accepts 400 changes; 200 keeps a single request comfortably
// under any proxy body limit while still making "paste a week" one round trip.
const CHUNK = 200;

let requestCounter = 0;
/** Autosave requests since the page loaded. Read by the criterion 31 run. */
export const autosaveRequestCount = () => requestCounter;

export function useWeekEditor({ jobId, weekStart }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Queued changes keyed so a second edit to the same cell replaces the first
  // rather than sending both.
  const queue = useRef(new Map());
  const timer = useRef(null);
  const inflight = useRef(false);
  const [pending, setPending] = useState(0);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const { data: d } = await schedulingAPI.week({
        job_id: jobId, week_start_date: weekStart,
      });
      setData(d);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load that week');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jobId, weekStart]);

  useEffect(() => { load(); }, [load]);

  const applyServerShifts = useCallback((shiftDays, totals, employees) => {
    setData((prev) => (prev ? {
      ...prev,
      shift_days: shiftDays ?? prev.shift_days,
      totals: totals ?? prev.totals,
      // The officers the week references come back with every batch. Without
      // merging them a newly assigned cell renders the fallback label until
      // somebody reloads the page, which is most of the point of the grid.
      employees: employees ?? prev.employees,
    } : prev));
  }, []);

  const flush = useCallback(async () => {
    if (inflight.current) return;
    const weekId = data?.week?.id;
    if (!weekId || queue.current.size === 0) return;
    const entries = Array.from(queue.current.entries()).slice(0, CHUNK);
    entries.forEach(([k]) => queue.current.delete(k));
    setPending(queue.current.size);
    inflight.current = true;
    setSaving(true);
    try {
      requestCounter += 1;
      const { data: res } = await schedulingAPI.bulkUpsert(weekId, {
        changes: entries.map(([, c]) => c.change),
      });
      applyServerShifts(res.shift_days, res.totals, res.employees);
      setLastSavedAt(new Date());
      if (res.blocked?.length) {
        setBlocked((prev) => [
          ...prev,
          ...res.blocked.map((b) => ({
            ...b,
            // The change the server refused, so a dialog can re-send it.
            change: entries[b.index]?.[1]?.change,
            key: entries[b.index]?.[0],
          })).filter((b) => b.change),
        ]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail
        || 'Those edits could not be saved. Nothing has been lost — try again.');
      // Put them back: losing typing is worse than a duplicate attempt.
      entries.forEach(([k, c]) => { if (!queue.current.has(k)) queue.current.set(k, c); });
      setPending(queue.current.size);
    } finally {
      inflight.current = false;
      setSaving(false);
      if (queue.current.size) {
        timer.current = setTimeout(() => flush(), 50);
      }
    }
  }, [data?.week?.id, applyServerShifts]);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    if (queue.current.size >= 60) { flush(); return; }
    timer.current = setTimeout(() => flush(), FLUSH_IDLE_MS);
  }, [flush]);

  /** Queue one change. `key` collapses repeated edits to the same cell. */
  const enqueue = useCallback((key, change, optimistic) => {
    queue.current.set(key, { change });
    setPending(queue.current.size);
    if (optimistic) {
      setData((prev) => (prev ? { ...prev, shift_days: optimistic(prev.shift_days) } : prev));
    }
    schedule();
  }, [schedule]);

  const enqueueMany = useCallback((items) => {
    items.forEach(({ key, change }) => queue.current.set(key, { change }));
    setPending(queue.current.size);
    schedule();
  }, [schedule]);

  const saveNow = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    await flush();
  }, [flush]);

  // A manager closing the tab mid-batch should not silently lose the tail.
  useEffect(() => {
    const warn = (e) => {
      if (queue.current.size === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const shiftsById = useMemo(() => {
    const m = new Map();
    (data?.shift_days || []).forEach((s) => m.set(s.id, s));
    return m;
  }, [data?.shift_days]);

  const removeBlocked = useCallback((keys) => {
    const drop = new Set(keys);
    setBlocked((prev) => prev.filter((b) => !drop.has(b.key)));
  }, []);

  const clearBlocked = useCallback(() => setBlocked([]), []);

  return {
    data, loading, saving, error, pending, blocked, lastSavedAt, shiftsById,
    reload: load, enqueue, enqueueMany, saveNow, clearBlocked, removeBlocked,
    setError,
  };
}
